import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Hace que un `<textarea>` crezca con su contenido en lugar de quedarse en una caja fija de dos o
 * tres líneas que obliga a escribir mirando por una rendija.
 *
 * Reparto de responsabilidades: aquí SOLO se escribe la altura medida del contenido, que es un valor
 * dinámico y no puede vivir en una hoja de estilos. Los TOPES son estilo y viven en el CSS
 * (`.msg-autogrow` en `recognition.css`): `min-height` fija el piso de 4 líneas, `max-height` el
 * techo de 10 y `overflow-y: auto` entrega el scroll cuando el mensaje pasa de ahí. Por eso el hook
 * no necesita conocer la altura de línea ni recortar nada — el navegador aplica los topes sobre la
 * altura que este hook pide.
 *
 * `height = 'auto'` antes de medir no es adorno: sin ese reset, `scrollHeight` nunca baja de la
 * altura que ya tiene el elemento y la caja crece pero jamás se encoge al borrar texto.
 *
 * @param reference Ref al textarea que se ajusta.
 * @param value Valor actual del textarea; cada cambio dispara una nueva medición.
 */
export function useAutoGrowTextarea(
  reference: RefObject<HTMLTextAreaElement | null>,
  value: string,
): void {
  useEffect(() => {
    const textarea = reference.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [reference, value]);
}
